import React, { useCallback, useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { targetedOutreachService } from '../services/targetedOutreachService';
import Layout from '../components/Layout';
import PasswordChangeModal from '../components/PasswordChangeModal';
import ScopeFilterBar from '../components/ScopeFilterBar';
import FitScoreExplainer from '../components/FitScoreExplainer';
import ScoringBanner from '../components/ScoringBanner';
import { useScoringState } from '../hooks/useScoringState';

/**
 * Targeted Outreach — which client to call today, and why.
 *
 * The page leads with a BUCKET and the evidence sentences, and carries the
 * priority score alongside them. That ordering is the design, and it matches
 * what Candidate Graph settled on: the bucket says whether to act, the
 * sentences say why — "you placed 3 people there" is something a recruiter can
 * check — and the number breaks ties inside a band, which is where most of the
 * work happens.
 *
 * The legacy page's score was indefensible because its two inputs were a
 * name-matched candidate's own fit and a signal produced from a URL that was
 * never fetched. The fix was the evidence underneath, not hiding the number:
 * every point here traces to a factor, and the drawer shows that breakdown.
 */

const BUCKETS = [
  { id: 'call_now', label: 'Call now', hint: 'Strong evidence and live demand', score: 'bg-emerald-100 text-emerald-800' },
  { id: 'warm', label: 'Warm', hint: 'Real history, less urgency', score: 'bg-amber-100 text-amber-800' },
  { id: 'track', label: 'Track', hint: 'Some evidence, worth watching', score: 'bg-sky-100 text-sky-800' },
];

/** The two buckets a PERSON produces rather than the model. They are kept apart
 *  from the three above because they are not a claim about the company — they
 *  are a record of a decision someone made about it, and mixing the two would
 *  read as the model having demoted a company nobody asked it to. */
const PARKED = [
  { id: 'snoozed', label: 'Snoozed', hint: 'Parked until a date, then back by itself' },
  { id: 'not_relevant', label: 'Not relevant', hint: 'Dismissed until someone reopens it' },
];

const bucketMeta = (id) => BUCKETS.find((b) => b.id === id) || { score: 'bg-gray-100 text-gray-700' };

/** The contact verbs the API accepts, in the order a recruiter reaches for them. */
const CONTACT_ACTIONS = [
  { id: 'call', label: 'Called' },
  { id: 'email', label: 'Emailed' },
  { id: 'meeting', label: 'Met' },
  { id: 'note', label: 'Note' },
];

const ACTION_LABEL = {
  call: 'Called', email: 'Emailed', meeting: 'Met', note: 'Note', other: 'Logged',
  snooze: 'Snoozed', not_relevant: 'Marked not relevant', reopen: 'Reopened',
};

/** The four factors, in the order the model declares them. Maxes come from the
 *  API rather than being hardcoded here, so recalibrating the algebra moves the
 *  bars instead of quietly making them lie. */
const FACTOR_ROWS = [
  { key: 'relationship', max: 'relationship_max', label: 'Relationship', hint: 'Placements and candidates you have put forward' },
  { key: 'conversion', max: 'conversion_max', label: 'Conversion', hint: 'How often what you send them actually gets placed' },
  { key: 'demand', max: 'demand_max', label: 'Demand', hint: 'Open roles and how soon the client needs them' },
  { key: 'external_signal', max: 'external_signal_max', label: 'External signal', hint: 'What we found outside your ATS' },
  { key: 'warm_path', max: 'warm_path_max', label: 'Warm path', hint: 'People you placed who now work there' },
];

function daysSince(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((Date.now() - then.getTime()) / 86400000);
}

/** Undated is shown as undated, with the date we SPOTTED it beside it.
 *
 *  Two things are deliberate. The `undated` flag comes from the API rather than
 *  being inferred from a null `occurred_at`, so the client and the server cannot
 *  disagree about it. And an undated row still gets a date -- "undated · spotted
 *  3 Aug" -- because "undated" alone reads as a bug, while a relative time
 *  ("2 days ago") would be the exact invention that made every legacy signal
 *  meaningless. 99.7% of what the crawler finds arrives undated, so this is the
 *  normal case, not an edge one. */
function signalWhen(signal) {
  if (signal?.undated || !signal?.occurred_at) {
    const spotted = signal?.first_seen_at ? formatDay(signal.first_seen_at) : null;
    return spotted ? `undated · spotted ${spotted}` : 'undated';
  }
  const d = daysSince(signal.occurred_at);
  if (d === null) return 'undated';
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d} days ago`;
  if (d < 365) return `${Math.floor(d / 30)} months ago`;
  return `${Math.floor(d / 365)} years ago`;
}

function formatDay(iso) {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const SIGNAL_LABEL = {
  job_posting: 'Hiring',
  leadership_change: 'Leadership',
  funding: 'Funding',
  expansion: 'Expansion',
};

export default function TargetedOutreach() {
  const [user, setUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [bucket, setBucket] = useState('call_now');
  // Set once the recruiter picks a tab themselves, so the auto-jump below never
  // overrides a deliberate choice.
  const [bucketChosen, setBucketChosen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState('');
  const [actionError, setActionError] = useState('');
  const [movements, setMovements] = useState(null);
  const [movementsOpen, setMovementsOpen] = useState(false);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const openMovements = async () => {
    if (!selected) return;
    setMovementsOpen(true);
    setMovementsLoading(true);
    try {
      const res = await targetedOutreachService.getMovements(selected.company_id);
      setMovements(res.movements || []);
    } catch {
      // A failed popup must not take the drawer down: the evidence behind it is
      // still true and still the reason the recruiter opened it.
      setMovements([]);
    } finally {
      setMovementsLoading(false);
    }
  };

  const [scope, setScope] = useState(null);
  // Deliberately component state and nothing else -- no localStorage, no URL
  // parameter the browser restores. Every visit opens on the desk, so one click
  // last month cannot leave a recruiter in the firehose for weeks.
  const [dims, setDims] = useState('both');
  // `search` is what the box shows; `query` is what the list was fetched with.
  // Debouncing between them keeps a 100-row fetch off every keystroke.
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      if (!userData) {
        window.location.href = '/login';
        return;
      }
      setUser(userData);
      if (userData.must_change_password) setShowPasswordModal(true);
    } catch {
      window.location.href = '/login';
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  useEffect(() => {
    let live = true;
    authService
      .getMyScope()
      .then((s) => live && setScope(s))
      // A desk that fails to load must not take the page down with it: the list
      // still works, it is just unscoped, which is the safe direction to fail.
      .catch(() => live && setScope(null));
    return () => {
      live = false;
    };
  }, []);

  const loadList = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      setData(await targetedOutreachService.getList({ bucket, limit: 100, dims, search: query }));
    } catch (e) {
      setError(e.message || 'Could not load your outreach list');
    } finally {
      setLoading(false);
    }
  }, [bucket, user, dims, query]);

  useEffect(() => { loadList(); }, [loadList]);

  // `data` already carries the whole list response, scoring_state included --
  // no separate state needed. Defaults to 'ready' so the banner stays out of
  // the way before the first response lands, the same window `loading`
  // already covers.
  const scoringState = data?.scoring_state || 'ready';
  const scoringMessage = data?.scoring_message || '';
  const { elapsedSeconds } = useScoringState(scoringState, loadList);

  // Open on a tab that has something in it.
  //
  // The bucket is a filter, not a page -- a recruiter works "call now" then
  // "warm" -- so defaulting to call_now is right. But outreach-v2's bands are
  // still uncalibrated placeholders (call_now needs 60+), and with a strict desk
  // almost nothing reaches them: a 6-company desk opened on "No companies in
  // this bucket" while all 6 sat one tab over. Landing on an empty tab reads as
  // "the feature is broken", which is the one thing the numbers are not.
  //
  // Only ever moves BEFORE the recruiter has chosen a tab, and only when the
  // current one is empty and another is not -- so it cannot fight a deliberate
  // click, and cannot loop.
  useEffect(() => {
    if (bucketChosen || !data?.buckets) return;
    const counts = data.buckets;
    if (counts[bucket]) return;
    const next = ['call_now', 'warm', 'track'].find((b) => counts[b] > 0);
    if (next && next !== bucket) setBucket(next);
  }, [data, bucket, bucketChosen]);

  const openDrawer = async (item) => {
    setSelected(item);
    setDetail(null);
    setActionError('');
    setMovements(null);
    setMovementsOpen(false);
    setDetailLoading(true);
    try {
      setDetail(await targetedOutreachService.getCompany(item.company_id));
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * Run one of the four verbs, then re-read both the drawer and the list.
   *
   * Re-reading rather than patching local state is deliberate: whether a company
   * is suppressed is derived by the server from the action log, so the only
   * honest source of "is it still on the list" is the list. Guessing here is how
   * a UI ends up disagreeing with the API about what the user just did.
   */
  const runAction = async (verb, fn) => {
    if (!selected) return;
    setActionBusy(verb);
    setActionError('');
    try {
      await fn(selected.company_id);
      setDetail(await targetedOutreachService.getCompany(selected.company_id));
      await loadList();
    } catch (e) {
      setActionError(e.message || 'That did not go through');
    } finally {
      setActionBusy('');
    }
  };

  if (!user) return null;

  const items = data?.items || [];
  const counts = data?.buckets || {};

  return (
    <Layout user={user}>
      {showPasswordModal && (
        <PasswordChangeModal
          user={user}
          onPasswordChanged={async () => setUser(await authService.getCurrentUser())}
          onClose={() => setShowPasswordModal(false)}
        />
      )}

      <div className="space-y-5">
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Targeted Outreach</h2>
            <p className="text-sm text-gray-600 mt-1">
              Companies worth a call, ranked on the evidence in your own ATS — who you have
              placed, who is hiring now, and who already knows you.
            </p>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search all companies on this list..."
            className="w-72 max-w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {BUCKETS.map((b) => (
            <button
              key={b.id}
              onClick={() => { setBucketChosen(true); setBucket(b.id); }}
              title={b.hint}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                bucket === b.id
                  ? 'bg-[#1B3A6B] text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {b.label}
              {counts[b.id] !== undefined && (
                <span className={`ml-2 text-xs ${bucket === b.id ? 'text-white/70' : 'text-gray-400'}`}>
                  {counts[b.id]}
                </span>
              )}
            </button>
          ))}

          <span className="h-6 w-px bg-gray-300 mx-1" aria-hidden="true" />

          {PARKED.map((b) => (
            <button
              key={b.id}
              onClick={() => { setBucketChosen(true); setBucket(b.id); }}
              title={b.hint}
              className={`px-3 py-2 rounded-lg text-sm ${
                bucket === b.id
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {b.label}
              {counts[b.id] !== undefined && (
                <span className={`ml-2 text-xs ${bucket === b.id ? 'text-white/70' : 'text-gray-400'}`}>
                  {counts[b.id]}
                </span>
              )}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
        )}

        <ScopeFilterBar scope={scope} dims={dims} onChange={setDims} />

        <FitScoreExplainer
          model={data?.model}
          title="How the priority score works"
          noun="company"
          intro={
            <>
              A priority score is built from{' '}
              <strong className="font-medium text-gray-800">evidence you already hold</strong> —
              your own record of who you have placed there, what they are hiring for, and who
              inside already knows you. It is not a judgement about the company, and it is not a
              guess about their business. Five factors add up to at most{' '}
              <strong className="font-medium text-gray-800">{data?.model?.max_base}</strong>, then
              reachability scales the total.
            </>
          }
        />

        <ScoringBanner
          state={scoringState}
          message={scoringMessage}
          elapsedSeconds={elapsedSeconds}
          onRetry={loadList}
        />

        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm p-6 text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            // A page still computing or failed must never look the same as a
            // genuinely empty result -- the banner above already explains that
            // case, so the ordinary empty state is suppressed until scoring
            // has actually settled on 'ready'.
            scoringState === 'ready' ? (
              <div className="bg-white rounded-xl shadow-sm p-6 text-gray-500">
                {/* Absence is a real answer: a company with no evidence gets no row at all. */}
                No companies in this bucket. Nothing here means we have no evidence worth acting on —
                not that the list failed to load.
              </div>
            ) : null
          ) : (
            items.map((item) => (
              <div
                key={item.company_id}
                role="button"
                tabIndex={0}
                onClick={() => openDrawer(item)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openDrawer(item); }}
                data-testid="outreach-row"
                className="w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:border-gray-300 p-5 transition-all"
              >
                <div className="flex gap-4">
                  <ScoreBadge score={item.priority_score} bucket={item.bucket} />
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                      {[item.city, item.country].filter(Boolean).length > 0 && (
                        <span className="text-sm text-gray-500">
                          {[item.city, item.country].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </div>

                    {/* The evidence, as sentences. This is the product. */}
                    <ul className="space-y-1">
                      {(item.reasons || []).map((reason, i) => (
                        <li key={i} className="text-sm text-gray-700 flex gap-2">
                          <span className="text-[#11B7A8] mt-0.5">•</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-wrap gap-2 pt-1">
                      {item.open_jobs > 0 && <Chip tone="blue">{item.open_jobs} open</Chip>}
                      {item.placements > 0 && <Chip tone="green">{item.placements} placed</Chip>}
                      {item.warm_paths > 0 && <Chip tone="teal">{item.warm_paths} warm</Chip>}
                      {item.new_signals > 0 && (
                        <span data-testid="new-signals-marker">
                          <Chip tone="amber">
                            {item.new_signals === 1 ? '1 new signal' : `${item.new_signals} new signals`}
                          </Chip>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <button
            aria-label="Close"
            className="absolute inset-0 bg-black/30"
            onClick={() => setSelected(null)}
          />
          <div data-testid="outreach-drawer" className="ml-auto w-full max-w-xl h-full bg-white shadow-2xl relative overflow-y-auto p-6 space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">{selected.name}</h3>
                {selected.website && (
                  <a
                    href={selected.website.startsWith('http') ? selected.website : `https://${selected.website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-[#2B7CFF]"
                  >
                    {selected.website}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-3">
                <ScoreBadge score={selected.priority_score} bucket={selected.bucket} size="w-12 h-12 text-lg" />
                <button
                  data-testid="outreach-what-moved"
                  onClick={openMovements}
                  className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
                >
                  What moved
                </button>
                <button className="text-gray-500 hover:text-gray-700 text-xl leading-none" onClick={() => setSelected(null)}>×</button>
              </div>
            </div>

            {/* The actions come FIRST in the drawer. The evidence below answers
                "should I call them"; this answers "I just did", and burying it
                under four sections of reasoning is how a log stops being kept. */}
            <Section title="Record what you did">
              {actionError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
                  {actionError}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {CONTACT_ACTIONS.map((a) => (
                  <button
                    key={a.id}
                    disabled={!!actionBusy}
                    data-testid={`outreach-action-${a.id}`}
                    onClick={() => runAction(a.id, (id) =>
                      targetedOutreachService.logAction(id, { action: a.id }))}
                    className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {actionBusy === a.id ? 'Saving...' : a.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-100">
                {/* Reopen is offered only when there is something to reopen, so
                    the drawer never shows two contradictory states at once. */}
                {(detail?.item?.dismissed || detail?.item?.snoozed_until) ? (
                  <button
                    disabled={!!actionBusy}
                    data-testid="outreach-action-reopen"
                    onClick={() => runAction('reopen', targetedOutreachService.reopen)}
                    className="px-3 py-1.5 rounded-lg text-sm bg-[#1B3A6B] text-white hover:bg-[#16305a] disabled:opacity-50"
                  >
                    {actionBusy === 'reopen' ? 'Saving...' : 'Put back on the list'}
                  </button>
                ) : (
                  <>
                    <button
                      disabled={!!actionBusy}
                      data-testid="outreach-action-snooze"
                      onClick={() => runAction('snooze', (id) => targetedOutreachService.snooze(id))}
                      className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {actionBusy === 'snooze' ? 'Saving...' : 'Snooze 7 days'}
                    </button>
                    <button
                      disabled={!!actionBusy}
                      data-testid="outreach-action-not-relevant"
                      onClick={() => runAction('not_relevant', (id) => targetedOutreachService.markNotRelevant(id))}
                      className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {actionBusy === 'not_relevant' ? 'Saving...' : 'Not relevant'}
                    </button>
                  </>
                )}
              </div>

              {detail?.item?.snoozed_until && (
                <p className="text-xs text-gray-500">
                  Snoozed until {detail.item.snoozed_until} — it comes back on its own.
                </p>
              )}
              {detail?.item?.dismissed && (
                <p className="text-xs text-gray-500">
                  Marked not relevant. It stays off the list until someone puts it back.
                </p>
              )}
            </Section>

            {detail?.actions?.length > 0 && (
              <Section title="What we have done">
                <ul className="space-y-2">
                  {detail.actions.map((a, i) => (
                    <li key={i} className="text-sm flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium text-gray-900">
                        {ACTION_LABEL[a.action] || a.action}
                      </span>
                      <span className="text-xs text-gray-500">{signalWhen({ occurred_at: a.occurred_at })}</span>
                      {a.user_name && <span className="text-xs text-gray-500">· {a.user_name}</span>}
                      {a.note && <span className="text-gray-700 basis-full">{a.note}</span>}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <Section title="Priority score">
              <FactorBreakdown score={selected.priority_score} factors={selected.factors} />
            </Section>

            {/* ABOVE the reasons, deliberately. A hiring freeze, a round of
                layoffs or a profit warning is something a recruiter needs BEFORE
                they dial -- reading it after four reasons to call is reading it
                too late. It is also why a risk scores nothing rather than
                subtracting: it changes what you SAY, not whether you call. */}
            {(detail?.signals || []).some((s) => s.direction === 'risk') && (
              <div data-testid="signal-caution"
                   className="border border-[#F0B429]/40 bg-[#F0B429]/10 rounded-xl p-4 space-y-2">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-[#8A6D1F]">
                  Before you call
                </h4>
                <ul className="space-y-2">
                  {/* === 'risk' and never !== 'opportunity': a NULL direction is
                      a signal judged before the column existed, and filing it
                      as a caution would invent a warning nobody wrote. */}
                  {detail.signals.filter((s) => s.direction === 'risk').map((s, i) => (
                    <li key={i} className="text-sm">
                      <p className="text-gray-800">{s.summary || s.title}</p>
                      <span className="text-xs text-gray-500">
                        {signalWhen(s)}
                        {s.source_name && ` · ${s.source_name}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Section title="Why this company">
              <ul className="space-y-1.5">
                {(selected.reasons || []).map((r, i) => (
                  <li key={i} className="text-sm text-gray-700 flex gap-2">
                    <span className="text-[#11B7A8] mt-0.5">•</span><span>{r}</span>
                  </li>
                ))}
              </ul>
            </Section>

            {detailLoading && <p className="text-sm text-gray-500">Loading evidence...</p>}

            {detail?.warm?.length > 0 && (
              <Section title="People you placed who now work there">
                <ul className="space-y-2">
                  {detail.warm.map((p) => (
                    <li key={p.person_id} className="text-sm">
                      <span className="font-medium text-gray-900">{p.full_name}</span>
                      {p.now_title && <span className="text-gray-600"> — {p.now_title}</span>}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {detail?.signals?.length > 0 && (
              <Section title="Spotted outside your ATS">
                <ul className="space-y-3">
                  {detail.signals.map((s, i) => (
                    <li key={i} className="text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <Chip tone="amber">{SIGNAL_LABEL[s.signal_type] || s.signal_type}</Chip>
                        <span data-testid="signal-when" className="text-gray-500 text-xs">
                          {signalWhen(s)}
                        </span>
                        {s.source_name && (
                          <span className="text-gray-400 text-xs">
                            ·{' '}
                            {s.source_url ? (
                              <a href={s.source_url} target="_blank" rel="noreferrer"
                                 data-testid="signal-source" className="hover:underline">
                                {s.source_name}
                              </a>
                            ) : (
                              <span data-testid="signal-source">{s.source_name}</span>
                            )}
                          </span>
                        )}
                      </div>
                      {/* The BD read is the line. It says what the event MEANS
                          for an agency; the raw page title is demoted to the
                          link beneath it, where it still lets a recruiter check
                          the claim against the source in one glance. */}
                      <p className="text-gray-800 mt-1">{s.summary || s.title}</p>
                      {s.summary && (
                        <p className="text-gray-500 text-xs mt-0.5">
                          {s.url ? (
                            <a href={s.url} target="_blank" rel="noreferrer" className="hover:underline">{s.title}</a>
                          ) : s.title}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {!detailLoading && !detail?.signals?.length && !detail?.warm?.length && (
              <p className="text-sm text-gray-500">
                No external signals or warm paths recorded yet for this company.
              </p>
            )}
          </div>

          {movementsOpen && (
            <MovementsPopup
              loading={movementsLoading}
              movements={movements}
              onClose={() => setMovementsOpen(false)}
            />
          )}
        </div>
      )}
    </Layout>
  );
}

/** The number sits BESIDE the bucket, not instead of it. The bucket answers
 *  "should I act"; the score answers "which of these two first" — which the
 *  bucket alone cannot, since most of a book lands in one band. */
function ScoreBadge({ score, bucket, size = 'w-11 h-11 text-base' }) {
  return (
    <span
      data-testid="outreach-score"
      title="Priority score"
      className={`${size} shrink-0 rounded-lg flex items-center justify-center font-bold ${bucketMeta(bucket).score}`}
    >
      {score}
    </span>
  );
}

/** Every point traced to the factor that earned it. Reachability is shown as a
 *  MULTIPLIER on its own line rather than as a bar, because it is not evidence:
 *  having a contact cannot raise a company on its own, it can only halve a
 *  score when missing. */
function FactorBreakdown({ score, factors }) {
  if (!factors) return null;
  return (
    <div className="space-y-3">
      {FACTOR_ROWS.map((row) => {
        const value = factors[row.key] ?? 0;
        const max = factors[row.max] ?? 0;
        const pct = max > 0 ? Math.round((value / max) * 100) : 0;
        return (
          <div key={row.key} className="space-y-1">
            <div className="flex items-baseline justify-between text-xs">
              <span className="font-medium text-gray-700" title={row.hint}>{row.label}</span>
              <span className="text-gray-500 tabular-nums">{value} / {max}</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
              <div className="h-full bg-[#1B3A6B]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}

      <div className="pt-2 border-t border-gray-200 space-y-1 text-xs">
        <div className="flex justify-between text-gray-600">
          <span>Evidence subtotal</span>
          <span className="tabular-nums">{factors.base}</span>
        </div>
        <div className="flex justify-between text-gray-600">
          <span title="A contact is a precondition for calling, not a reason to call">
            Reachability
          </span>
          <span className="tabular-nums">
            × {factors.reachability}
            {factors.reachability < 1 && <span className="text-amber-700"> (no contact)</span>}
          </span>
        </div>
        <div className="flex justify-between font-semibold text-gray-900 pt-1">
          <span>Priority score</span>
          <span className="tabular-nums">{score}</span>
        </div>
      </div>
    </div>
  );
}

/** What happened, and what it did to the score — one merged timeline.
 *
 *  The list arrives already merged and ordered from the API. That is
 *  deliberate: interleaving signals and score moves is a rule about data (an
 *  undated signal orders by when we saw it), and a rule about data belongs
 *  somewhere a server test can reach. This component only renders. */
function MovementsPopup({ loading, movements, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button aria-label="Close" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between">
          <h4 className="text-lg font-semibold text-gray-900">What moved</h4>
          <button className="text-gray-500 hover:text-gray-700 text-xl leading-none" onClick={onClose}>×</button>
        </div>

        {loading && <p className="text-sm text-gray-500">Loading...</p>}

        {!loading && movements && movements.length === 0 && (
          <p className="text-sm text-gray-500">
            Nothing has moved for this company yet. That is a real answer — no external
            signal has been judged and the score has not changed.
          </p>
        )}

        <ol className="space-y-3">
          {(movements || []).map((m, i) => (
            <li key={i} data-testid="movement-row" className="text-sm border-l-2 border-gray-200 pl-3">
              {m.kind === 'signal' ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip tone="amber">{SIGNAL_LABEL[m.signal_type] || m.signal_type}</Chip>
                    {/* A timeline that cannot tell a funding round from a
                        hiring freeze is an undifferentiated list, which is the
                        "everything is good news" failure this slice removes. */}
                    {m.direction === 'risk' && <Chip tone="amber">Caution</Chip>}
                    <span className="text-xs text-gray-500">
                      {signalWhen({ occurred_at: m.undated ? null : m.at, undated: m.undated, first_seen_at: m.at })}
                    </span>
                    {m.source_name && <span className="text-xs text-gray-400">· {m.source_name}</span>}
                  </div>
                  <p className="text-gray-800 mt-1">{m.summary || m.title}</p>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip tone="blue">Score</Chip>
                    <span className="text-xs text-gray-500">{signalWhen({ occurred_at: m.at })}</span>
                  </div>
                  <p className="text-gray-800 mt-1">
                    {m.prev_score !== null && m.prev_score !== undefined
                      ? `${m.prev_score} → ${m.score}`
                      : `First scored at ${m.score}`}
                    {m.prev_bucket && m.prev_bucket !== m.bucket && (
                      <span className="text-gray-600"> · {m.prev_bucket} → {m.bucket}</span>
                    )}
                  </p>
                  {/* Attribution, derived server-side by diffing the factor
                      breakdown between consecutive move-log rows. Shown only
                      when it actually moved -- "external signal +0" is noise. */}
                  {m.external_signal_delta > 0 && (
                    <p className="text-xs text-[#0E8C81] mt-0.5">
                      external signal +{m.external_signal_delta}
                    </p>
                  )}
                </>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
      {children}
    </div>
  );
}

function Chip({ tone = 'blue', children }) {
  const tones = {
    blue: 'bg-[#2B7CFF]/10 text-[#1B3A6B]',
    green: 'bg-[#0B956B]/10 text-[#0B956B]',
    teal: 'bg-[#11B7A8]/10 text-[#0E8C81]',
    amber: 'bg-[#F0B429]/15 text-[#8A6D1F]',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}
